import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { Mail, ChevronDown, ChevronUp, AlertCircle, CheckCircle2, Loader2, Maximize2, X, User } from "lucide-react";
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import { getSenderByKey } from "@/lib/leadSenders";

interface LeadEmailLog {
  id: string;
  email_type: string;
  subject: string;
  body_preview: string | null;
  status: string | null;
  sent_at: string | null;
  resend_email_id: string | null;
  error_message: string | null;
  sender_key: string | null;
}

interface LeadEmailHistoryDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  leadId: string;
  leadName: string;
}

// Helper: detect if content is already a full HTML document
function isFullHtmlDocument(content: string): boolean {
  const lower = content.toLowerCase().trim();
  return lower.startsWith("<!doctype") || lower.startsWith("<html");
}

// Helper: detect if content seems truncated
function isTruncated(content: string | null): boolean {
  if (!content) return false;
  // Truncated if exactly 500 chars or doesn't have closing tags
  return content.length === 500 || 
    (!content.includes("</html>") && !content.includes("</body>"));
}

// Helper: build srcDoc for iframe
function buildSrcDoc(content: string): string {
  // If it's already a full HTML document, use it as-is
  if (isFullHtmlDocument(content)) {
    return content;
  }
  
  // Otherwise wrap it in a minimal HTML structure
  return `
    <!DOCTYPE html>
    <html>
      <head>
        <meta charset="utf-8">
        <meta name="viewport" content="width=device-width, initial-scale=1">
        <style>
          body { 
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
            font-size: 14px;
            margin: 0;
            padding: 16px;
            background: white;
          }
          img { max-width: 100%; height: auto; }
          a { color: #2563eb; }
        </style>
      </head>
      <body>${content}</body>
    </html>
  `;
}

export function LeadEmailHistoryDialog({
  open,
  onOpenChange,
  leadId,
  leadName,
}: LeadEmailHistoryDialogProps) {
  const [emails, setEmails] = useState<LeadEmailLog[]>([]);
  const [loading, setLoading] = useState(false);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [fullscreenEmail, setFullscreenEmail] = useState<LeadEmailLog | null>(null);
  
  // Cache for fetched full HTML content
  const [fullHtmlCache, setFullHtmlCache] = useState<Record<string, string>>({});
  const [loadingEmailId, setLoadingEmailId] = useState<string | null>(null);

  useEffect(() => {
    if (open && leadId) {
      fetchEmails();
      // Reset caches when dialog opens
      setFullHtmlCache({});
      setExpandedId(null);
    }
  }, [open, leadId]);

  const fetchEmails = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from("lead_email_logs")
        .select("*")
        .eq("lead_id", leadId)
        .order("sent_at", { ascending: false });

      if (error) throw error;
      setEmails(data || []);
    } catch (error) {
      console.error("Error fetching lead emails:", error);
    } finally {
      setLoading(false);
    }
  };

  // Fetch full HTML for truncated emails
  const fetchFullHtml = async (emailId: string): Promise<string | null> => {
    // Check cache first
    if (fullHtmlCache[emailId]) {
      return fullHtmlCache[emailId];
    }

    setLoadingEmailId(emailId);
    try {
      const { data, error } = await supabase.functions.invoke("get-lead-email-html", {
        body: { leadEmailLogId: emailId },
      });

      if (error) {
        console.error("Error fetching full HTML:", error);
        return null;
      }

      if (data?.success && data?.html) {
        // Update cache
        setFullHtmlCache(prev => ({ ...prev, [emailId]: data.html }));
        
        // If backfilled, update the email in state too
        if (data.backfilled) {
          setEmails(prev => prev.map(e => 
            e.id === emailId ? { ...e, body_preview: data.html } : e
          ));
        }
        
        return data.html;
      }
      return null;
    } catch (error) {
      console.error("Error fetching full HTML:", error);
      return null;
    } finally {
      setLoadingEmailId(null);
    }
  };

  // Get the best available content for an email
  const getEmailContent = (email: LeadEmailLog): string | null => {
    // Check cache first
    if (fullHtmlCache[email.id]) {
      return fullHtmlCache[email.id];
    }
    return email.body_preview;
  };

  const getEmailTypeLabel = (type: string) => {
    switch (type) {
      case "first_contact":
        return { label: "1er contact", icon: "✉️", color: "bg-blue-50 text-blue-700 border-blue-200" };
      case "followup":
        return { label: "Relance", icon: "🔄", color: "bg-amber-50 text-amber-700 border-amber-200" };
      default:
        return { label: type, icon: "📧", color: "bg-gray-50 text-gray-700 border-gray-200" };
    }
  };

  const handleToggleExpand = async (email: LeadEmailLog) => {
    const newExpandedId = expandedId === email.id ? null : email.id;
    setExpandedId(newExpandedId);
    
    // If expanding and content seems truncated, fetch the full version
    if (newExpandedId && isTruncated(email.body_preview) && !fullHtmlCache[email.id]) {
      await fetchFullHtml(email.id);
    }
  };

  const handleFullscreen = async (email: LeadEmailLog) => {
    // If content seems truncated and not cached, fetch full version first
    if (isTruncated(email.body_preview) && !fullHtmlCache[email.id]) {
      const fullHtml = await fetchFullHtml(email.id);
      if (fullHtml) {
        setFullscreenEmail({ ...email, body_preview: fullHtml });
        return;
      }
    }
    setFullscreenEmail(email);
  };

  // Fullscreen email view
  if (fullscreenEmail) {
    const typeInfo = getEmailTypeLabel(fullscreenEmail.email_type);
    const content = getEmailContent(fullscreenEmail);
    
    return (
      <Dialog open={true} onOpenChange={() => setFullscreenEmail(null)}>
        <DialogContent className="max-w-4xl w-[95vw] h-[90vh] flex flex-col p-0 gap-0">
          <div className="flex items-center justify-between p-4 border-b bg-muted/30">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <Badge variant="outline" className={typeInfo.color}>
                  {typeInfo.icon} {typeInfo.label}
                </Badge>
                {fullscreenEmail.status === "sent" ? (
                  <span className="flex items-center gap-1 text-xs text-green-600">
                    <CheckCircle2 className="w-3 h-3" />
                    Envoyé
                  </span>
                ) : fullscreenEmail.status === "failed" ? (
                  <span className="flex items-center gap-1 text-xs text-destructive">
                    <AlertCircle className="w-3 h-3" />
                    Échec
                  </span>
                ) : null}
                <span className="text-xs text-muted-foreground">
                  {fullscreenEmail.sent_at
                    ? format(new Date(fullscreenEmail.sent_at), "dd MMM yyyy 'à' HH:mm", { locale: fr })
                    : "—"}
                </span>
                <Badge variant="secondary" className="text-xs gap-1">
                  <User className="w-3 h-3" />
                  {getSenderByKey(fullscreenEmail.sender_key).shortName}
                </Badge>
              </div>
              <p className="font-medium text-sm">{fullscreenEmail.subject}</p>
            </div>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setFullscreenEmail(null)}
              className="shrink-0"
            >
              <X className="w-4 h-4" />
            </Button>
          </div>
          <div className="flex-1 overflow-hidden bg-white">
            {loadingEmailId === fullscreenEmail.id ? (
              <div className="flex items-center justify-center h-full">
                <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
                <span className="ml-2 text-muted-foreground">Chargement du contenu complet...</span>
              </div>
            ) : content ? (
              <iframe
                srcDoc={buildSrcDoc(content)}
                className="w-full h-full border-0"
                title="Email content"
                sandbox="allow-same-origin allow-popups allow-popups-to-escape-sandbox"
              />
            ) : (
              <div className="p-8 text-center text-muted-foreground">
                Contenu non disponible
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Mail className="w-5 h-5 text-primary" />
            Emails envoyés à {leadName}
          </DialogTitle>
          <DialogDescription>
            {emails.length} email{emails.length !== 1 ? "s" : ""} envoyé{emails.length !== 1 ? "s" : ""}
          </DialogDescription>
        </DialogHeader>

        <ScrollArea className="flex-1 -mx-6 px-6">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
            </div>
          ) : emails.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              Aucun email envoyé à ce prospect
            </div>
          ) : (
            <div className="space-y-3 py-2">
              {emails.map((email) => {
                const typeInfo = getEmailTypeLabel(email.email_type);
                const isExpanded = expandedId === email.id;
                const content = getEmailContent(email);
                const isLoading = loadingEmailId === email.id;

                return (
                  <Collapsible
                    key={email.id}
                    open={isExpanded}
                    onOpenChange={() => handleToggleExpand(email)}
                  >
                    <div className="border rounded-lg overflow-hidden">
                      <div className="p-4 bg-muted/30">
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              <Badge variant="outline" className={typeInfo.color}>
                                {typeInfo.icon} {typeInfo.label}
                              </Badge>
                              {email.status === "sent" ? (
                                <span className="flex items-center gap-1 text-xs text-green-600">
                                  <CheckCircle2 className="w-3 h-3" />
                                  Envoyé
                                </span>
                              ) : email.status === "failed" ? (
                                <span className="flex items-center gap-1 text-xs text-destructive">
                                  <AlertCircle className="w-3 h-3" />
                                  Échec
                                </span>
                              ) : null}
                            </div>
                            <p className="font-medium text-sm truncate" title={email.subject}>
                              {email.subject}
                            </p>
                            {email.error_message && (
                              <p className="text-xs text-destructive mt-1">
                                {email.error_message}
                              </p>
                            )}
                          </div>
                          <div className="flex items-center gap-2 shrink-0">
                            <Badge variant="secondary" className="text-xs gap-1">
                              <User className="w-3 h-3" />
                              {getSenderByKey(email.sender_key).shortName}
                            </Badge>
                            <span className="text-xs text-muted-foreground whitespace-nowrap">
                              {email.sent_at
                                ? format(new Date(email.sent_at), "dd MMM yyyy 'à' HH:mm", { locale: fr })
                                : "—"}
                            </span>
                          </div>
                        </div>

                        {email.body_preview && (
                          <div className="flex items-center gap-2 mt-2">
                            <CollapsibleTrigger asChild>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-7 text-xs"
                                disabled={isLoading}
                              >
                                {isLoading ? (
                                  <>
                                    <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                                    Chargement...
                                  </>
                                ) : isExpanded ? (
                                  <>
                                    <ChevronUp className="w-3 h-3 mr-1" />
                                    Masquer
                                  </>
                                ) : (
                                  <>
                                    <ChevronDown className="w-3 h-3 mr-1" />
                                    Aperçu
                                  </>
                                )}
                              </Button>
                            </CollapsibleTrigger>
                            <Button
                              variant="outline"
                              size="sm"
                              className="h-7 text-xs"
                              onClick={() => handleFullscreen(email)}
                              disabled={isLoading}
                            >
                              <Maximize2 className="w-3 h-3 mr-1" />
                              Plein écran
                            </Button>
                          </div>
                        )}
                      </div>

                      <CollapsibleContent>
                        {content && (
                          <div className="border-t h-[400px] overflow-hidden">
                            {isLoading ? (
                              <div className="flex items-center justify-center h-full">
                                <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
                              </div>
                            ) : (
                              <iframe
                                srcDoc={buildSrcDoc(content)}
                                className="w-full h-full border-0 bg-white"
                                title="Email preview"
                                sandbox="allow-same-origin allow-popups allow-popups-to-escape-sandbox"
                              />
                            )}
                          </div>
                        )}
                      </CollapsibleContent>
                    </div>
                  </Collapsible>
                );
              })}
            </div>
          )}
        </ScrollArea>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Fermer
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
